import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import dayjs from 'dayjs';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Expense } from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
const ExpenseEntryDialog = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const { ticket, onAction, action, actionExtras } = useContext(TicketContext);
  const [expense, setExpense] = useState<Expense>({
    id: '',
    category: '',
    amount: 0,
    exchangeRate: 1,
    currency: 'USD',
    billable: false,
    expenseDate: dayjs().toISOString(),
    description: '',
    createdAt: '',
    createdBy: '',
  });
  const [form] = Form.useForm();
  useEffect(() => {
    if (isNotNull(actionExtras)) {
      try {
        const extras: Expense = JSON.parse(actionExtras);
        if (extras.id !== null && extras.id !== '') {
          setExpense(extras);

          form.setFieldsValue({
            category: extras.category,
            amount: extras.amount,
            exchangeRate: extras.exchangeRate,
            currency: extras.currency,
            billable: extras.billable,
            description: extras.description,
          });
        }
      } catch {
        /* empty */
      }
    }
  }, [actionExtras]);

  useEffect(() => {
    if (expense.currency === 'USD' && expense.exchangeRate !== 1) {
      setExpense((prevState) => ({
        ...prevState,
        exchangeRate: 1,
      }));
    }
  }, [expense.currency, expense.exchangeRate]);
  const sendExpense = useCallback(
    async (paramTicketId: string, payload: Expense) => {
      if (!isSending) {
        setIsSending(true);

        payload.createdBy = userProfile.email;

        payload.createdAt = dayjs().toISOString();

        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/expenses`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            form.resetFields();

            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const handleSubmit = () => {
    sendExpense(ticket.ticket.id, expense);
  };
  return (
    <Modal
      open={action === 'show-expense-entry'}
      title="Add Expense Entry"
      onCancel={() => {
        form.resetFields();

        onAction('', '');
      }}
      onOk={handleSubmit}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();

            onAction('', '');
          }}
        >
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          category: expense.category,
          amount: expense.amount,
          exchangeRate: expense.exchangeRate,
          currency: expense.currency,
          billable: expense.billable,
          description: expense.description,
        }}
      >
        <Form.Item label="Category" name="category">
          <Select
            placeholder="Select category"
            value={expense.category}
            onChange={(value) =>
              setExpense((prevState) => ({
                ...prevState,
                category: value,
              }))
            }
          >
            <Select.Option value="Subcontracting">Subcontracting</Select.Option>
            <Select.Option value="Travel">Travel</Select.Option>
            <Select.Option value="Misc Expense">Misc Expense</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Date"
          name="expenseDate"
          rules={[{ required: true, message: 'Please select date' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            onChange={(newValue) =>
              setExpense((prevState) => ({
                ...prevState,
                expenseDate: newValue.toISOString(),
              }))
            }
          />
        </Form.Item>
        <Row gutter={24}>
          <Col span={16}>
            <Form.Item
              label="Amount"
              name="amount"
              rules={[
                { required: true, message: 'Please enter expense amount' },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                value={expense.amount}
                onChange={(value) =>
                  setExpense((prevState) => ({
                    ...prevState,
                    amount: value,
                  }))
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Billable" name="billable" valuePropName="checked">
              <Switch
                value={expense.billable}
                onChange={(checked: boolean) =>
                  setExpense((prevState) => ({
                    ...prevState,
                    billable: checked,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Currency" name="currency">
          <Select
            placeholder="Select currency"
            value={expense.currency}
            onChange={(value) =>
              setExpense((prevState) => ({
                ...prevState,
                currency: value,
              }))
            }
          >
            <Select.Option value="USD">USD</Select.Option>
            <Select.Option value="CAD">CAD</Select.Option>
            <Select.Option value="EUR">EUR</Select.Option>
          </Select>
        </Form.Item>
        {expense.currency !== 'USD' && (
          <Form.Item
            label="Exchange Rate (To USD)"
            name="exchangeRate"
            rules={[
              { required: true, message: 'Please enter exchange rate to USD' },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={expense.exchangeRate}
              onChange={(value) =>
                setExpense((prevState) => ({
                  ...prevState,
                  exchangeRate: value,
                }))
              }
            />
          </Form.Item>
        )}
        <Form.Item label="Description" name="description">
          <Input.TextArea
            rows={3}
            value={expense.description}
            onChange={(event) =>
              setExpense((prevState) => ({
                ...prevState,
                description: event.target.value,
              }))
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default ExpenseEntryDialog;
